import React, { useEffect, useState, useRef, useCallback } from 'react'
import { FormattedDate, FormattedTime } from 'react-intl'
import { MarkdownViewer, Button } from '@arnsbogroup/react-voicearchive'
import classNames from 'classnames'

const WhatsAppChat = ({ messages: _messages, onSend: _onSend }) => {
  const threadRef = useRef(null)
  const scrollAnchorRef = useRef(null)
  const [messages, setMessages] = useState([])
  const [inputValue, setInputValue] = useState('')

  useEffect(() => setMessages(_messages), [_messages])

  useEffect(() => {
    if (scrollAnchorRef.current) {
      const timer = setTimeout(() => {
        scrollAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
      }, 100)
      return () => clearTimeout(timer);
    }
  }, [messages])

  const onSend = useCallback(() => {
    const markdown = inputValue.trim()

    if (!markdown.length) {
      return window.alert('Message is empty!')
    }

    const nextMessage = {
      id: +Date.now(),
      timestamp: Math.floor(Date.now() / 1000),
      body: markdown,
      recipient: { type: 4 },
    }

    setMessages((prevMessages) => [...prevMessages, nextMessage])
    setInputValue('')

    _onSend(markdown)
  }, [_onSend, inputValue])

  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey && !isTouchDevice && inputValue?.trim().length) {
      e.preventDefault()
      onSend()
    }
  }, [onSend, inputValue, isTouchDevice])

  return (
    <div className="w-full h-full flex flex-col">
      <div className="grow overflow-y-auto px-4 md:px-6 py-4" ref={threadRef}>
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-gray-500 px-4">
              <svg
                className="mx-auto h-12 w-12 md:h-16 md:w-16 mb-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
              <p className="text-sm md:text-base">No messages yet. Start the conversation!</p>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-2">
              {messages.map(({ id, timestamp, body, recipient }) => {
                const isSent = recipient?.type > 1
                return (
                  <div
                    key={id}
                    className={classNames(
                      'flex',
                      isSent ? 'justify-end' : 'justify-start'
                    )}
                  >
                    <div className={classNames(
                      'max-w-[80%] md:max-w-[65%]'
                    )}>
                      <div
                        className={classNames(
                          'break-words px-3 py-2 md:px-4 md:py-2.5 text-sm md:text-base whitespace-pre-wrap shadow',
                          isSent
                            ? 'text-white rounded-lg rounded-tr-none'
                            : 'bg-white text-gray-900 rounded-lg rounded-tl-none'
                        )}
                        style={isSent ? { backgroundColor: '#25d366' } : {}}
                      >
                        <MarkdownViewer className={isSent ? 'prose-invert' : ''}>
                          {body}
                        </MarkdownViewer>
                      </div>
                      <div className={classNames(
                        'text-xs mt-0.5 px-1 text-gray-600',
                        isSent ? 'text-right' : 'text-left'
                      )}>
                        {timestamp > 0 ? (
                          <>
                            <FormattedDate value={new Date(timestamp * 1000)} />
                            {' '}
                            <FormattedTime value={new Date(timestamp * 1000)} />
                          </>
                        ) : (
                          <span className="italic">Sending...</span>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </>
        )}
      </div>

      <div className="px-4 md:px-5 py-2 md:py-3">
          <div className='w-full'>
            <textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Type a message..."
              className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm md:text-base"
              style={{
                outline: 'none',
                minHeight: '38px',
                maxHeight: '120px'
              }}
              onFocus={(e) => e.target.style.borderColor = '#25d366'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              rows={1}
            />
          </div>
          <div className='w-full md:w-auto flex justify-end'>
            <Button
              variant={inputValue?.length ? 'solid' : 'outline'}
              size='sm'
              disabled={!inputValue?.length}
              onClick={onSend}
              icon={['fa', 'paper-plane']}
              iconPosition='right'
            >
              Send
            </Button>
          </div>
          <div ref={scrollAnchorRef} />
      </div>
    </div>
  )
}

export default WhatsAppChat
